import type { CustomRoutePath, CustomRouteSegment } from '../../types/CustomRoute';

/**
 * cyrb53, a fast public-domain string hash by bryc (see jshash in https://github.com/bryc/code), returned in base 36. Not
 * cryptographic: it only has to tell different inputs apart, and at 53 bits a collision is not a practical concern. The constants are the algorithm's own: two arbitrary non-zero starting states, Knuth's multiplicative
 * constant and a second odd multiplier to mix each character in, and MurmurHash3's finalizer constants to spread the bits.
 */
const cyrb53 = (input: string) => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ char, 2654435761);
    h2 = Math.imul(h2 ^ char, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
};

// Built from the values alone, so a path rebuilt with its keys in another order, or with `freeDraw: false` in place of no
// `freeDraw`, is still the same line
const hashPaths = (paths: CustomRoutePath[]) =>
  cyrb53(paths.map(path => `${path.freeDraw ? 'f' : 'r'}${path.points.map(point => point.join(',')).join(';')}`).join('|'));

// Store state is never changed in place, so a `paths` array keeps its hash for as long as it exists. An edit to one segment
// replaces only that segment's array, so the others' hashes are not recomputed.
const hashesByPaths = new WeakMap<CustomRoutePath[], string>();

/**
 * A short fingerprint of a segment's paths. An AI summary stores the one for the paths it describes, so any later change to
 * the segment's line (a drag, reroute, cut, reverse...) marks the summary outdated, while edits elsewhere on the route do
 * not.
 */
export const getSegmentPathsHash = (paths: CustomRoutePath[]) => {
  let hash = hashesByPaths.get(paths);
  if (hash === undefined) {
    hash = hashPaths(paths);
    hashesByPaths.set(paths, hash);
  }
  return hash;
};

/**
 * - `none`: the description is not an AI summary (never generated, written by hand, or lost on reload)
 * - `current`: an AI summary of the segment's line as it is now
 * - `outdated`: an AI summary of a line the segment no longer has
 */
export type SegmentAiSummaryStatus = 'current' | 'none' | 'outdated';

export const getSegmentAiSummaryStatus = (segment: Pick<CustomRouteSegment, 'aiSummaryPathsHash' | 'paths'>): SegmentAiSummaryStatus => {
  if (!segment.aiSummaryPathsHash) {
    return 'none';
  }
  return segment.aiSummaryPathsHash === getSegmentPathsHash(segment.paths) ? 'current' : 'outdated';
};

/** Whether the segment has a line to describe; a segment just added has no points until it is drawn */
export const canSummarizeSegment = (segment: Pick<CustomRouteSegment, 'paths'>) =>
  segment.paths.reduce((count, path) => count + path.points.length, 0) >= 2;

/** Whether the segment has a line, and an AI summary of a line it no longer has */
export const hasOutdatedAiSummary = (segment: Pick<CustomRouteSegment, 'aiSummaryPathsHash' | 'paths'>) =>
  canSummarizeSegment(segment) && getSegmentAiSummaryStatus(segment) === 'outdated';

/** Whether generating would do anything: the segment has a line, and no AI summary of it as it is now */
export const needsAiSummary = (segment: Pick<CustomRouteSegment, 'aiSummaryPathsHash' | 'paths'>) =>
  canSummarizeSegment(segment) && getSegmentAiSummaryStatus(segment) !== 'current';
